import { useMemo, useRef, useState } from 'react';
import MatchSummaryHeader from './MatchSummaryHeader';
import CategoryOverview from './CategoryOverview';
import MatchCard from './MatchCard';
import PaywallModal from './PaywallModal';
import ResultsActionButtons from './ResultsActionButtons';
import { CATEGORIES } from '../data/questions';
import { formatMatchingDesiresLabel } from '../lib/resultsDisplay';
import { useCurrency } from '../contexts/CurrencyContext';
import type { Category, MatchItem, MatchResult } from '../types';

interface ResultsSuccessViewProps {
  data: MatchResult;
  partnerToken: string;
  resultsUrl: string;
  onPaymentComplete: (reason: 'payment-completed' | 'already-paid') => void;
  unlockButtonLabel?: string;
}

type CategoryViewModel = {
  id: Category;
  label: string;
  emoji: string;
  totalQuestions: number;
  totalMatches: number;
  visibleMatches: MatchItem[];
  isLocked: boolean;
};

export default function ResultsSuccessView({
  data,
  partnerToken,
  resultsUrl,
  onPaymentComplete,
  unlockButtonLabel,
}: ResultsSuccessViewProps) {
  const { config: currencyConfig } = useCurrency();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const premiumDesireLabel = data.premiumMatchCount === 1 ? 'desire' : 'desires';

  const categoryData = useMemo<CategoryViewModel[]>(() => {
    return CATEGORIES.map((cat) => {
      const catData = data.categories[cat.id];
      const totalMatches = catData?.totalMatches ?? 0;
      const visibleMatches = catData?.matches ?? [];

      return {
        id: cat.id,
        label: cat.label,
        emoji: cat.emoji,
        totalQuestions: catData?.totalQuestions ?? 0,
        totalMatches,
        visibleMatches,
        isLocked: !data.isPaid && visibleMatches.length < totalMatches,
      };
    });
  }, [data]);

  const totalQuestions = categoryData.reduce((sum, cat) => sum + cat.totalQuestions, 0);
  const sortedCategories = categoryData.filter((cat) => cat.totalMatches > 0);
  const categoryOverviewData = categoryData.map((cat) => ({
    id: cat.id,
    label: cat.label,
    emoji: cat.emoji,
    matchCount: cat.totalMatches,
    totalQuestions: cat.totalQuestions,
  }));

  function handleCategoryClick(category: Category) {
    const el = categoryRefs.current[category];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function handleComplete(reason: 'payment-completed' | 'already-paid') {
    setPaywallOpen(false);
    onPaymentComplete(reason);
  }

  return (
    <>
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        <MatchSummaryHeader
          totalMatches={data.totalMatches}
          totalQuestions={totalQuestions}
        />

        <section className="space-y-3 pt-2">
          <div className="text-center space-y-1">
            <h2 className="text-xl font-bold text-white">Your Adventure Map</h2>
            <p className="text-sm text-slate-400">Where your desires run the deepest</p>
          </div>

          <CategoryOverview
            categories={categoryOverviewData}
            onCategoryClick={handleCategoryClick}
          />
        </section>

        <section className="space-y-4 pt-2">
          <div className="text-center">
            <h2 className="text-xl font-bold text-white">
              Your {data.totalMatches} Shared {data.totalMatches === 1 ? 'Desire' : 'Desires'}
            </h2>
          </div>

          <ResultsActionButtons
            data={data}
            resultsUrl={resultsUrl}
          />

          <div className="space-y-8">
          {sortedCategories.map((cat) => {
            const lockedCount = cat.totalMatches - cat.visibleMatches.length;

            return (
              <div
                key={cat.id}
                ref={(el) => { categoryRefs.current[cat.id] = el; }}
                className="scroll-mt-20"
              >
                <h3 className="flex items-center gap-2 text-lg font-bold text-white mb-3">
                  <span>{cat.emoji}</span>
                  <span>{cat.label}</span>
                  <span className="text-sm font-normal text-slate-400">
                    {formatMatchingDesiresLabel(cat.totalMatches)}
                  </span>
                </h3>

                <div className="rounded-2xl border border-white/10 bg-purple-card overflow-hidden">
                  {/* Column headers with colored avatar circles */}
                  <div className="flex items-center gap-3 px-4 py-2 border-b border-white/10 bg-black/10">
                    <span className="min-w-0 flex-1" />
                    <span className="flex-shrink-0 w-9 sm:w-[50px] flex justify-center">
                      <span className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-[0.7rem] font-bold text-white">
                        {data.partnerAName.charAt(0)}
                      </span>
                    </span>
                    <span className="flex-shrink-0 w-9 sm:w-[50px] flex justify-center">
                      <span className="w-6 h-6 rounded-full bg-pink-500 flex items-center justify-center text-[0.7rem] font-bold text-white">
                        {data.partnerBName.charAt(0)}
                      </span>
                    </span>
                  </div>

                  {cat.visibleMatches.map((match) => (
                    <MatchCard
                      key={match.questionId}
                      match={match}
                      partnerAName={data.partnerAName}
                      partnerBName={data.partnerBName}
                    />
                  ))}

                  {lockedCount > 0 && (
                    <button
                      onClick={() => setPaywallOpen(true)}
                      className="relative w-full text-left group"
                    >
                      <div className="blur-[4px] select-none pointer-events-none opacity-40" aria-hidden="true">
                        {[0, 1].map((i) => (
                          <div key={i} className="flex items-center gap-3 px-4 py-3.5 border-t border-white/[0.07]">
                            <p className="min-w-0 flex-1 text-[0.9rem] font-medium text-white">A hidden shared desire...</p>
                            <span className="flex-shrink-0 w-9 sm:w-[50px] text-center text-xl">🔥</span>
                            <span className="flex-shrink-0 w-9 sm:w-[50px] text-center text-xl">🔥</span>
                          </div>
                        ))}
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-purple-card via-purple-card/60 to-transparent">
                        <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/10 text-sm font-medium text-slate-300 backdrop-blur-sm transition-colors group-hover:bg-white/[0.15] group-hover:text-white">
                          🔑 Reveal {lockedCount} more {lockedCount === 1 ? 'match' : 'matches'}
                        </span>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </section>

        {!data.isPaid && data.premiumMatchCount > 0 && (
          <div className="sticky bottom-4 z-40">
            <button
              onClick={() => setPaywallOpen(true)}
              className="w-full py-4 gradient-cta text-white font-bold rounded-2xl transition-all"
            >
              {unlockButtonLabel ?? `Reveal ${data.premiumMatchCount} more ${premiumDesireLabel} for ${currencyConfig.label}`}
            </button>
          </div>
        )}
      </div>

      <PaywallModal
        sessionId={data.sessionId}
        partnerToken={partnerToken}
        isOpen={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        onComplete={handleComplete}
      />
    </>
  );
}
